import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAdminSupportTickets, getAdminSupportTicket, replyAdminSupportTicket, updateAdminSupportTicket,
  creditUserWallet,
} from '../../api';
import { PageHeader, StatusBadge, Table, Pagination, SearchInput, EmptyState, TableSkeleton, UserCell, UserLink } from '../../components/ui';
import OverviewBar from '../../components/ui/OverviewBar';
import Modal from '../../components/ui/Modal';
import { formatDateTime, formatCurrency } from '../../utils/format';
import { Headphones, RefreshCw, Send, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';

const CATEGORIES = ['', 'payment', 'consultation', 'refund'];
const STATUSES = ['', 'open', 'replied', 'resolved'];

export default function SupportPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-support', page, status, category, search],
    queryFn: () => getAdminSupportTickets({
      page,
      limit: 20,
      status: status || undefined,
      category: category || undefined,
      search: search || undefined,
    }).then((r) => r.data),
  });

  const tickets = data?.data || [];
  const pagination = data?.pagination;

  return (
    <div className="p-6">
      <PageHeader title="Support Tickets" subtitle="Categories · assignment · reply · refund (PDF §33)">
        <button onClick={() => qc.invalidateQueries({ queryKey: ['admin-support'] })} className="btn-secondary btn-sm"><RefreshCw size={14} /></button>
      </PageHeader>

      <OverviewBar items={[
        { label: 'Total tickets', value: pagination?.total ?? '—', icon: <Headphones size={14} /> },
        { label: 'Open / replied', value: data?.meta?.open ?? '—', color: 'text-orange-600' },
      ]} loading={isLoading} />

      <div className="card p-4 mb-5 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[200px]"><SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="Search subject…" /></div>
        {STATUSES.map((s) => (
          <button key={s || 'all-s'} onClick={() => { setStatus(s); setPage(1); }} className={`px-3 py-1.5 rounded-lg border text-xs font-medium capitalize ${status === s ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 text-gray-600'}`}>{s || 'All status'}</button>
        ))}
        {CATEGORIES.map((c) => (
          <button key={c || 'all-c'} onClick={() => { setCategory(c); setPage(1); }} className={`px-3 py-1.5 rounded-lg border text-xs font-medium capitalize ${category === c ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 text-gray-600'}`}>{c || 'All categories'}</button>
        ))}
      </div>

      <div className="card overflow-hidden">
        {isLoading ? <div className="p-5"><TableSkeleton rows={8} cols={5} /></div>
          : tickets.length === 0 ? <EmptyState message="No support tickets" icon={<Headphones size={32} />} />
          : (
            <Table>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="table-th">User</th>
                  <th className="table-th">Subject</th>
                  <th className="table-th">Category</th>
                  <th className="table-th">Assignee</th>
                  <th className="table-th">Messages</th>
                  <th className="table-th">Status</th>
                  <th className="table-th">Updated</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t: any) => (
                  <tr key={t._id} className="table-row">
                    <td className="table-td"><UserCell entity={t.userId} /></td>
                    <td className="table-td text-sm font-medium">
                      <Link to={`/support/${t._id}`} className="hover:text-primary-600">{t.subject}</Link>
                    </td>
                    <td className="table-td text-xs capitalize">{t.category || '—'}</td>
                    <td className="table-td text-xs">{t.assignedTo?.name || t.assignedTo || '—'}</td>
                    <td className="table-td text-xs">{t.messages?.length || 0}</td>
                    <td className="table-td"><StatusBadge status={t.status} /></td>
                    <td className="table-td text-xs">{formatDateTime(t.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        {pagination?.pages > 1 && (
          <div className="p-4 border-t"><Pagination page={page} pages={pagination.pages} total={pagination.total} limit={20} onPageChange={setPage} /></div>
        )}
      </div>
    </div>
  );
}

export function SupportTicketPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [assignee, setAssignee] = useState('');
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('Support ticket refund');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-support-one', id],
    queryFn: () => getAdminSupportTicket(id!).then((r) => r.data.data.ticket),
    enabled: !!id,
  });

  const reply = useMutation({
    mutationFn: (payload: { message: string; resolve?: boolean }) => replyAdminSupportTicket(id!, payload),
    onSuccess: () => {
      toast.success('Reply sent');
      setText('');
      qc.invalidateQueries({ queryKey: ['admin-support-one', id] });
      qc.invalidateQueries({ queryKey: ['admin-support'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed'),
  });

  const setStatus = useMutation({
    mutationFn: (status: string) => updateAdminSupportTicket(id!, { status }),
    onSuccess: () => {
      toast.success('Status updated');
      qc.invalidateQueries({ queryKey: ['admin-support-one', id] });
    },
  });

  const assign = useMutation({
    mutationFn: () => updateAdminSupportTicket(id!, { assignedTo: assignee, status: data?.status || 'open' }),
    onSuccess: () => {
      toast.success('Assignment saved (persists if backend supports assignedTo)');
      qc.invalidateQueries({ queryKey: ['admin-support-one', id] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Assign failed'),
  });

  const refund = useMutation({
    mutationFn: async () => {
      const userId = data?.userId?._id || data?.userId;
      if (!userId) throw new Error('No user on ticket');
      const paise = Math.round(Number(refundAmount) * 100);
      await creditUserWallet(userId, paise, `${refundReason} (ticket ${id})`);
      await replyAdminSupportTicket(id!, { message: `Refund issued: ${formatCurrency(paise)}. ${refundReason}` });
    },
    onSuccess: () => {
      toast.success('Refund credited to user wallet');
      setRefundOpen(false);
      setRefundAmount('');
      qc.invalidateQueries({ queryKey: ['admin-support-one', id] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || e?.message || 'Refund failed'),
  });

  const t = data;
  if (isLoading) return <div className="p-6"><TableSkeleton rows={6} cols={1} /></div>;
  if (!t) return <div className="p-6"><EmptyState message="Ticket not found" /></div>;

  return (
    <div className="p-6 max-w-3xl">
      <PageHeader
        title={t.subject}
        subtitle={
          <UserLink entity={t.userId} className="text-sm text-primary-600 hover:underline font-medium">
            {t.userId?.name || t.userId?.phone || 'Open user'}
          </UserLink>
        }
      >
        <button onClick={() => navigate('/support')} className="btn-secondary btn-sm flex items-center gap-1"><ArrowLeft size={14} /> Inbox</button>
        <button onClick={() => setRefundOpen(true)} className="btn-secondary btn-sm">Issue refund</button>
      </PageHeader>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <StatusBadge status={t.status} />
        <span className="text-xs text-gray-500 capitalize">Category: {t.category || '—'}</span>
        {['open', 'replied', 'resolved'].map((s) => (
          <button key={s} onClick={() => setStatus.mutate(s)} className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 capitalize">{s}</button>
        ))}
      </div>

      <div className="card p-4 mb-4 flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-gray-600 mb-1">Assign to (admin id / name)</label>
          <input className="input" value={assignee} onChange={(e) => setAssignee(e.target.value)} placeholder={t.assignedTo?.name || t.assignedTo || 'Admin'} />
        </div>
        <button className="btn-secondary btn-sm" disabled={!assignee.trim() || assign.isPending} onClick={() => assign.mutate()}>Assign</button>
      </div>

      <div className="card p-4 mb-4 max-h-[480px] overflow-y-auto space-y-2">
        {(t.messages || []).map((m: any, i: number) => (
          <div key={i} className={`flex ${m.from === 'support' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.from === 'support' ? 'bg-primary-500 text-white' : 'bg-gray-100'}`}>
              <p className="text-[10px] opacity-70 mb-0.5">{m.from}</p>
              <p className="whitespace-pre-wrap">{m.text}</p>
              <p className="text-[10px] mt-1 opacity-70">{formatDateTime(m.createdAt)}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="card p-4">
        <textarea className="input min-h-[90px]" value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a reply…" />
        <div className="flex gap-2 mt-3">
          <button disabled={!text.trim() || reply.isPending} onClick={() => reply.mutate({ message: text })} className="btn-primary btn-sm flex items-center gap-1"><Send size={14} /> Send reply</button>
          <button disabled={!text.trim() || reply.isPending} onClick={() => reply.mutate({ message: text, resolve: true })} className="btn-secondary btn-sm">Send & resolve</button>
        </div>
      </div>

      <Modal open={refundOpen} onClose={() => setRefundOpen(false)} title="Issue wallet refund">
        <div className="space-y-3">
          <p className="text-xs text-gray-500">Credits the ticket user&apos;s wallet (paise stored). Records a support reply.</p>
          <div>
            <label className="block text-xs font-medium mb-1">Amount (₹)</label>
            <input type="number" min="1" className="input" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Reason</label>
            <input className="input" value={refundReason} onChange={(e) => setRefundReason(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={() => setRefundOpen(false)}>Cancel</button>
            <button className="btn-primary" disabled={!refundAmount || refund.isPending} onClick={() => refund.mutate()}>Credit refund</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
